/**
 * ExplainContext – global provider for the "Explain this screen" panel.
 *
 * The provider holds two pieces of state:
 *   - isOpen         → drives the right-side drawer
 *   - smartAiMode    → optional toggle (future-ready, currently no-op)
 *
 * Usage:
 *   const { openExplainPanel } = useExplain();
 *   <Button onClick={openExplainPanel}>Explain this screen</Button>
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ExplainContextValue {
  isOpen: boolean;
  smartAiMode: boolean;
  openExplainPanel: () => void;
  closeExplainPanel: () => void;
  toggleExplainPanel: () => void;
  setSmartAiMode: (v: boolean) => void;
}

const ExplainContext = createContext<ExplainContextValue | null>(null);

export function ExplainProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [smartAiMode, setSmartAiMode] = useState(false);

  const openExplainPanel = useCallback(() => setIsOpen(true), []);
  const closeExplainPanel = useCallback(() => setIsOpen(false), []);
  const toggleExplainPanel = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo<ExplainContextValue>(
    () => ({
      isOpen,
      smartAiMode,
      openExplainPanel,
      closeExplainPanel,
      toggleExplainPanel,
      setSmartAiMode,
    }),
    [isOpen, smartAiMode, openExplainPanel, closeExplainPanel, toggleExplainPanel],
  );

  return <ExplainContext.Provider value={value}>{children}</ExplainContext.Provider>;
}

export function useExplain(): ExplainContextValue {
  const ctx = useContext(ExplainContext);
  if (!ctx) {
    // Safe fallback so consumers don't crash if mounted outside provider.
    return {
      isOpen: false,
      smartAiMode: false,
      openExplainPanel: () => {},
      closeExplainPanel: () => {},
      toggleExplainPanel: () => {},
      setSmartAiMode: () => {},
    };
  }
  return ctx;
}
